import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { useProjectMembers } from '@/features/projects/public';
import { useProjectStatuses } from '../../hooks/useProjectStatuses';
import { useBoardGroups, useCreateBoardGroup } from '@/features/boards/public';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Input } from '@/shared/components/ui/input';
import { Button } from '@/shared/components/ui/button';
import type { GroupByOption, ProjectStatus, TaskListViewConfig, TaskFilters } from '../../types';
import type { BoardGroup } from '@/features/boards/public';
import { GroupTable } from './GroupTable';

// Values match the API priority integer keys: 4=Urgent, 3=High, 2=Medium, 1=Low, 0=None
const PRIORITY_GROUPS: { value: string; labelKey: string }[] = [
  { value: '4', labelKey: 'priority.urgent' },
  { value: '3', labelKey: 'priority.high' },
  { value: '2', labelKey: 'priority.medium' },
  { value: '1', labelKey: 'priority.low' },
  { value: '0', labelKey: 'priority.none' },
];

interface GroupDef {
  value: string;
  label: string;
  color?: string;
}

interface TaskListViewProps {
  projectId: string;
  boardId: string;
  config: TaskListViewConfig;
  columnWidths: Record<string, number>;
  filters: TaskFilters;
  onConfigChange: (config: TaskListViewConfig) => void;
  onColumnsChange: (columns: TaskListViewConfig['visibleColumns']) => void;
  onColumnWidthsChange: (widths: Record<string, number>) => void;
}

function NewGroupButton({ boardId }: { boardId: string }) {
  const { t } = useTranslation('tasks');
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const { mutate: createGroup, isPending } = useCreateBoardGroup(boardId);

  function handleCreate() {
    const trimmed = name.trim();
    if (!trimmed) return;
    createGroup(
      { name: trimmed },
      {
        onSuccess: () => {
          setName('');
          setOpen(false);
        },
      },
    );
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreate();
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="tasks-new-group-btn"
          className="flex w-full items-center gap-2 px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground"
        >
          <Plus className="size-3.5 shrink-0" />
          {t('groupHeader.newGroup')}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 p-2"
        data-testid="tasks-new-group-popover"
        onClick={(e) => e.stopPropagation()}
      >
        <Input
          data-testid="tasks-new-group-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('groupHeader.newGroupNamePlaceholder')}
          className="mb-2 h-7 text-xs"
          autoFocus
          onKeyDown={handleKeyDown}
        />
        <div className="flex justify-end gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            data-testid="tasks-new-group-cancel-btn"
            onClick={() => setOpen(false)}
          >
            {t('groupHeader.newGroupCancel')}
          </Button>
          <Button
            size="sm"
            className="h-6 px-2 text-xs"
            data-testid="tasks-new-group-create-btn"
            disabled={!name.trim() || isPending}
            onClick={handleCreate}
          >
            {t('groupHeader.newGroupCreate')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function TaskListView({
  projectId,
  boardId,
  config,
  columnWidths,
  filters,
  onConfigChange,
  onColumnsChange,
  onColumnWidthsChange,
}: TaskListViewProps) {
  const { t } = useTranslation('tasks');

  const { data: members = [] } = useProjectMembers(projectId);
  const { statuses } = useProjectStatuses(boardId);
  const { groups: boardGroups } = useBoardGroups(boardId);

  const groupDefs = resolveGroupDefs(config.groupBy, boardGroups, statuses, members, t);

  const firstStatusId = statuses[0]?.id;

  if (config.groupBy === 'none') {
    return (
      <div data-testid="tasks-listview-root" className="flex flex-col overflow-auto pt-4">
        <GroupTable
          projectId={projectId}
          boardId={boardId}
          groupBy="none"
          groupValue=""
          groupLabel=""
          config={config}
          columnWidths={columnWidths}
          filters={filters}
          onConfigChange={onConfigChange}
          onColumnsChange={onColumnsChange}
          onColumnWidthsChange={onColumnWidthsChange}
          defaultStatusId={firstStatusId}
          unwrapped
        />
      </div>
    );
  }

  return (
    <div data-testid="tasks-listview-root" className="flex flex-col gap-6 overflow-auto pt-4">
      {groupDefs.map((group) => (
        <GroupTable
          key={group.value}
          projectId={projectId}
          boardId={boardId}
          groupBy={config.groupBy}
          groupValue={group.value}
          groupLabel={group.label}
          groupColor={group.color}
          config={config}
          columnWidths={columnWidths}
          filters={filters}
          onConfigChange={onConfigChange}
          onColumnsChange={onColumnsChange}
          onColumnWidthsChange={onColumnWidthsChange}
          defaultStatusId={config.groupBy === 'status' ? group.value : firstStatusId}
        />
      ))}

      {config.groupBy === 'group' && <NewGroupButton boardId={boardId} />}
    </div>
  );
}

function resolveGroupDefs(
  groupBy: GroupByOption,
  boardGroups: BoardGroup[],
  statuses: ProjectStatus[],
  members: { userId: string; displayName: string }[],
  t: (key: string) => string,
): GroupDef[] {
  if (groupBy === 'group') {
    const defs: GroupDef[] = [...boardGroups]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((g) => ({
        value: g.id,
        label: g.name,
        color: ('color' in g ? g.color : undefined) as string | undefined,
      }));
    // API uses "" as key for tasks with no group
    defs.push({ value: '', label: t('listView.noGroup') });
    return defs;
  }

  if (groupBy === 'status') {
    return [...statuses]
      .sort((a, b) => a.order - b.order)
      .map((s) => ({ value: s.id, label: s.name, color: s.color }));
  }

  if (groupBy === 'priority') {
    return PRIORITY_GROUPS.map((g) => ({ value: g.value, label: t(g.labelKey) }));
  }

  if (groupBy === 'assignee') {
    const defs: GroupDef[] = members.map((m) => ({
      value: m.userId,
      label: m.displayName,
    }));
    // API uses "" as key for unassigned tasks
    defs.push({ value: '', label: t('listView.noAssignee') });
    return defs;
  }

  return [];
}
